import { memo, useState, type ChangeEvent, type DragEvent, type FC } from "react";
import { Paperclip, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { formatNumber } from "src/utils/numberFormat";
import CampaignStepCard from "src/layouts/Engagements/Campaigns/shared/CampaignStepCard";
import { CSV_EXTENSION, MAX_FILE_ROWS, MAX_FILE_SIZE_BYTES } from "../createList.constants";
import { formatFileSize, type ParsedCsv } from "../createListCsv";
import { useCreateListLabel } from "../useCreateListLabel";

const BYTES_PER_MB = 1024 * 1024;

interface UploadFileStepProps {
  csv: ParsedCsv | null;
  parsing: boolean;
  onSelect: (_file: File) => void;
  onRemove: () => void;
}

const UploadFileStep: FC<UploadFileStepProps> = ({ csv, parsing, onSelect, onRemove }) => {
  const { i18n } = useTranslation();
  const label = useCreateListLabel();
  const [isDragging, setIsDragging] = useState(false);
  const isLocked = Boolean(csv) || parsing;
  const formatValue = (value: number) => formatNumber(value, {}, i18n.language);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (file) {
      onSelect(file);
    }
  };

  const handleDragOver = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    if (!isLocked) {
      setIsDragging(true);
    }
  };

  const handleDrop = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (file && !isLocked) {
      onSelect(file);
    }
  };

  return (
    <CampaignStepCard title={label("upload.title")} subtitle={label("upload.subtitle")}>
      <label
        className={`create-list__dropzone ${isLocked ? "create-list__dropzone--locked" : ""} ${
          isDragging ? "create-list__dropzone--dragging" : ""
        }`}
        onDragOver={handleDragOver}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        aria-disabled={isLocked}
      >
        <input
          type="file"
          accept={CSV_EXTENSION}
          hidden
          disabled={isLocked}
          onChange={handleChange}
        />
        <img src="/Icons/upload-arrow.svg" alt="" className="create-list__dropzone-icon" />
        <span className="create-list__dropzone-title">
          {parsing ? label("upload.reading") : label("upload.dropTitle")}
        </span>
        <span className="create-list__dropzone-hint">
          {label("upload.dropHint", {
            rows: formatValue(MAX_FILE_ROWS),
            size: formatValue(MAX_FILE_SIZE_BYTES / BYTES_PER_MB),
          })}
        </span>
      </label>

      <p className="create-list__muted">{label("upload.fileNeeds")}</p>

      {csv && (
        <div className="create-list__file">
          <span className="create-list__file-icon" aria-hidden>
            <Paperclip size={16} />
          </span>
          <div className="create-list__file-copy">
            <span className="create-list__file-name">{csv.file.name}</span>
            <span className="create-list__file-meta">
              {label("upload.fileMeta", {
                rows: formatValue(csv.rows.length),
                size: formatFileSize(csv.file.size, formatValue),
              })}
            </span>
          </div>
          <button
            type="button"
            className="create-list__icon-btn create-list__icon-btn--danger"
            aria-label={label("upload.removeFile")}
            onClick={onRemove}
          >
            <Trash2 size={18} />
          </button>
        </div>
      )}
    </CampaignStepCard>
  );
};

export default memo(UploadFileStep);
